import { useContext, useState, useEffect } from "react";
import css from "../css/approve-modal.module.css";
import { FaTimes as CancelIcon } from "react-icons/fa";
import { RiGlobalFill as TopIcon } from "react-icons/ri";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { NotificationContext } from "../../../../providers/notification";
import * as Yup from "yup";
import apiCallFormData from "../../../../utils/apiFormDataCall";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ExNyRoute,
  PrRoute,
  ExRoute,
  AcRoute,
  MiRoute,
  CoRoute,
} from "../../../../utils/allRouteId";
import { FilterDropDown } from "../../../../ui-components";
import apiCall from "../../../../utils/apiCall";

export const filterRouteName = (input: number) => {
  if (!input) return "N/A";
  const routes = [
    { routeId: Number(ExNyRoute), routeName: "Exam/Nysc Route" },
    { routeId: Number(PrRoute), routeName: "Practitioner Route" },
    { routeId: Number(ExRoute), routeName: "Executive Route" },
    { routeId: Number(AcRoute), routeName: "Academic Route" },
    { routeId: Number(MiRoute), routeName: "Military Route" },
    { routeId: Number(CoRoute), routeName: "Corporate Route" },
  ];

  const result = routes.filter(
    (item: { routeId: number; routeName: string }) => item.routeId == input
  )[0].routeName;
  return result;
};

interface ApprovalHistory {
  ActionTypeId: number;
  RouteId: number;
  AppMemberId: string;
  TargetedMembers?: any;
  TargetedMembersId: number;
  ApprovalRoleId: string;
  Email: string;
  ApprovalIndex: number;
  Status: number;
  FinalApproval: number;
  ApprovalComment: string;
  ApprovalActionTypes: number;
  ApprovalAction: string;
  ApprovalActionTypeId?: any;
  examId?: number;
}

interface ExamEntryLevelsData {
  Id: number;
  Name: string;
  RouteId: number;
  ExamAmount: number;
  ExemptionAmount: number;
  IsActive: boolean;
}

const validationSchema = Yup.object().shape({
  comment: Yup.string().required("Commment name is required"),
});

const Approve = (props: any) => {
  const onChange = props.onChange;
  const [comment, setComment] = useState<any>("");
  const [EntryLevelId, setEntryLevelId] = useState<number>();
  const [entryLevelname, setEntryLevelName] = useState("");
  const data = props.data;
  const ApprovalHistoryData = props?.ApprovalHistoryData as any[];
  const userId = useSelector((state: RootState) => state?.user?.userId);
  const { showNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();

  //Approve
  const postApproval = async (data: FormData) => {
    return await apiCallFormData().post("/Approval/NextApproval", data);
  };

  const getAllExamEntryLevels = async (): Promise<ExamEntryLevelsData[]> => {
    return (await apiCall().get("/SetUp/GetAllExemption"))?.data
      ?.Data as ExamEntryLevelsData[];
  };

  const { data: entryLevelData } = useQuery({
    queryKey: ["get-exam-entry-levels"],
    queryFn: getAllExamEntryLevels,
  });

  const approvalMutation = useMutation(["post-approval"], postApproval, {
    retry: 0,
  });

  const handleSubmitApproval = async (action: string) => {
    const body = new FormData();

    body.append("ApprovalTypeId", "1");
    body.append("RouteId", data?.RouteId);
    body.append("AppMemberId", data?.AppMemberId);
    body.append("TargetedMembers", "Applicants");
    body.append("TargetedMembersId", data?.TargetedMembersId);
    body.append("ApprovalRoleId", data?.ApprovalRoleId);
    body.append("ApprovalIndex", data?.ApprovalIndex);
    body.append("Status", data?.Status);
    body.append("FinalApproval", data?.FinalApproval);
    body.append("ApprovalComment", comment);
    body.append("ApprovalActionTypeId", "1");
    body.append("ApprovalActionTypes", action);
    body.append("ApprovalAction", action);
    body.append("Email", data?.Email);
    body.append("ApprovalUserId", userId);
    data?.RouteId == ExNyRoute &&
      body.append("ExemptionId", String(EntryLevelId));
    try {
      if (data?.RouteId == ExNyRoute && !EntryLevelId)
        throw "Set Exam Entry Level";

      await approvalMutation.mutateAsync(body, {
        onSuccess: () => {
          showNotification({
            message: "Approval Successful",
            type: "success",
          });
          queryClient.invalidateQueries(["get-approval"]);
          onChange(false);
        },
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error.message || error,
        type: "error",
      });
    }
  };

  const postDecline = async (data: FormData) => {
    return await apiCallFormData().post("/Approval/ApprovalDeclined", data);
  };

  const declineMutation = useMutation(["post-decline"], postDecline, {
    retry: 0,
  });

  const handleSubmitDecline = async (action: string) => {
    const response = await validationSchema
      .isValid({
        comment: comment,
      })
      .then((valid) => valid);

    const body = new FormData();

    body.append("ApprovalTypeId", "1");
    body.append("RouteId", data?.RouteId);
    body.append("AppMemberId", data?.AppMemberId);
    body.append("TargetedMembers", "Applicants");
    body.append("TargetedMembersId", data?.TargetedMembersId);
    body.append("ApprovalRoleId", data?.ApprovalRoleId);
    body.append("ApprovalIndex", data?.ApprovalIndex);
    body.append("Status", data?.Status);
    body.append("FinalApproval", data?.FinalApproval);
    body.append("ApprovalComment", comment);
    body.append("ApprovalActionTypeId", "2");
    body.append("ApprovalActionTypes", action);
    body.append("ApprovalAction", action);
    body.append("Email", data?.Email);
    body.append("ApprovalUserId", userId);

    try {
      if (!response) throw "Comment is Required";
      await declineMutation.mutateAsync(body, {
        onSuccess: () => {
          showNotification({
            message: "Declined Successful",
            type: "success",
          });
          queryClient.invalidateQueries(["get-approval"]);
          onChange(false);
        },
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error.message || error,
        type: "error",
      });
    }
  };

  let content = <Loading>Loading...</Loading>;

  const handleApproval = () => {
    handleSubmitApproval("Approved");
  };

  const handleReject = () => {
    handleSubmitDecline("Rejected");
  };

  const SwitchEntryLevel = (data: { val: number; name: string }) => {
    setEntryLevelName(data?.name);
    setEntryLevelId(data?.val);
  };

  const entryLevelOptions = entryLevelData
    ? entryLevelData?.map((item: ExamEntryLevelsData) => {
        return {
          val: item?.Id,
          name: item?.Name,
        };
      })
    : [];


  useEffect(() => {
    if (ApprovalHistoryData && ApprovalHistoryData?.length > 0) {
      const ApprovalHistoryNew = ApprovalHistoryData;
      const lastHistory = ApprovalHistoryNew.slice(-1)[0];

      const filteredExameEntryLevelData = entryLevelOptions.filter(
        (item: { val: number; name: string }) =>
          item?.val == lastHistory?.examId
      );

      if (filteredExameEntryLevelData?.length < 1) return;

      SwitchEntryLevel(filteredExameEntryLevelData[0]);
    }
  }, [ApprovalHistoryData]);

  return (
    <div className={css.container}>
      <span className={css.cancel} onClick={() => onChange(false)}>
        <CancelIcon />
      </span>
      <div>
        <span>
          <TopIcon />
        </span>
        <div>Approval Form</div>
      </div>
      <div>
        <div className={css.title}>Details</div>
        <div>
          <span className={css.label}>Full Name</span>
          <span>
            {data?.FirstName} {data?.LastName}
          </span>
        </div>
        <div>
          <span className={css.label}>approval ID</span>
          <span>{data?.ApprovalRoleId}</span>
        </div>
        <div>
          <span className={css.label}>Membership Route</span>
          <span>{data?.RouteName}</span>
        </div>
      </div>
      <div>
        <div className={css.title}>Approval History</div>
        <div
          style={
            data?.RouteId == ExNyRoute
              ? { gridTemplateColumns: "0.6fr 1.2fr 1.5fr 1fr 1fr" }
              : {}
          }
        >
          <span>Approval Level</span>
          <span>Email Address</span>
          <span>Comment</span>
          <span>Approval Status</span>
          {data?.RouteId == ExNyRoute && <span>Exam Entry Level</span>}
        </div>
        <div>
          {ApprovalHistoryData?.length > 0
            ? ApprovalHistoryData.map(
                (eData: ApprovalHistory, index: number) => (
                  <EachHistory
                    key={index}
                    approvalLevel={eData.ApprovalIndex + 1}
                    email={eData.Email}
                    comment={eData.ApprovalComment}
                    status={eData.ApprovalAction}
                    entryLevel={eData?.examId}
                    routeId={eData.RouteId}
                    entryLevelData={entryLevelData}
                  />
                )
              )
            : "History not available"}
        </div>
      </div>
      {data?.RouteId == ExNyRoute && (
        <FilterDropDown
          onChange={SwitchEntryLevel}
          width100={true}
          text={entryLevelname ? entryLevelname : "Exam Entry Levels"}
          bg="#0A3922"
          color="#fff"
          options={entryLevelOptions}
        />
      )}
      <div className={css.comment}>
        <div className={css.title}>Comment</div>
        <textarea
          rows={6}
          value={comment}
          onChange={(e: any) => setComment(e.target.value)}
        ></textarea>
      </div>
      <div className={css.btnDiv}>
        <button
          disabled={declineMutation.isLoading || approvalMutation.isLoading}
          style={{
            border: "1px solid #EB5757",
            color: "#EB5757",
            cursor: "pointer",
          }}
          onClick={handleReject}
        >
          {declineMutation.isLoading ? "Declining..." : "Decline"}
        </button>

        <StyledButton
          onClick={handleApproval}
          style={{
            border: "1px solid #5BC065",
            color: "#fff",
            backgroundColor: "#5BC065",
            cursor: "pointer",
          }}
          disabled={approvalMutation.isLoading || declineMutation.isLoading}
          data-testid="approve"
        >
          {approvalMutation.isLoading ? "Approving..." : "Approve"}
        </StyledButton>
      </div>
    </div>
  );
};

export default Approve;

export const EachHistory = (props: any) => {
  const approvalLevel = props.approvalLevel;
  const email = props.email;
  const approvalType = props.approvalType;
  const comment = props.comment;
  const status = props.status;
  const entryLevel = props.entryLevel;
  const routeId = props.routeId;
  const entryLevelData = props.entryLevelData;

  const entryLevelName = (input: number) => {
    if (!input || !entryLevelData) return "N/A";
    const result = entryLevelData.filter(
      (item: { Id: number; name: string }) => item.Id == input
    );
    return result.length > 0 ? result[0].Name : "N/A";
  };

  return (
    <div
      style={
        routeId == ExNyRoute
          ? { gridTemplateColumns: "0.6fr 1.2fr 1.5fr 1fr 1fr" }
          : {}
      }
    >
      <span>{approvalLevel}</span>
      <span>{email}</span>
      <span>{comment || "N/A"}</span>
      <span>{status}</span>
      {routeId == ExNyRoute && <span>{entryLevelName(entryLevel)}</span>}
    </div>
  );
};

export const Loading = styled.div`
  width: 100%;
  display: flex;
  min-height: 200px;
  justify-content: center;
  align-items: center;
`;

const StyledButton = styled.button`
  cursor: pointer;
`;
